import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Navigation, CheckCircle, ArrowRight, Building2, User, 
  Mail, Phone, Car, Sparkles, Shield, Clock, Gift
} from "lucide-react";
import { toast } from "sonner";

export default function FreeTrialPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    company_address: "",
    admin_name: "",
    admin_email: "",
    admin_phone: "",
    password: "",
    confirm_password: "",
    vehicle_count: "1-5"
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post("/auth/register-trial", {
        company_name: formData.company_name,
        company_address: formData.company_address,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_phone: formData.admin_phone,
        password: formData.password,
        vehicle_count: formData.vehicle_count
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        setUser(response.data.user);
        toast.success("Welcome! Your 14-day free trial has started!");
        navigate("/employer");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    { icon: Car, text: "Unlimited rides during trial" },
    { icon: User, text: "Add unlimited drivers & clients" },
    { icon: Shield, text: "Full access to all features" },
    { icon: Clock, text: "14 days to explore everything" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-5xl">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left Side - Benefits */}
          <div className="text-white space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Navigation className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-cyan-300 via-white to-purple-300 bg-clip-text text-transparent">There You Are</h1>
                <p className="text-cyan-300 text-sm font-semibold">Medical Transportation</p>
              </div>
            </div>

            <div className="space-y-2">
              <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-400/40 text-amber-300 px-4 py-2">
                <Gift className="w-4 h-4 mr-2 inline" />
                14-Day Free Trial
              </Badge>
              <h2 className="text-4xl font-black leading-tight">
                Start Your Free Trial
                <span className="block text-cyan-400">No Credit Card Required</span>
              </h2>
              <p className="text-slate-300 text-lg">
                Get full access to all features for 14 days. Experience how There You Are 
                can transform your medical transportation operations.
              </p>
            </div>

            <div className="space-y-4 pt-4">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                    <benefit.icon className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-slate-200">{benefit.text}</span>
                </div>
              ))}
            </div>

            <div className="pt-6 border-t border-slate-700">
              <p className="text-slate-400 text-sm">
                After your trial ends, choose a plan that fits your fleet size. 
                <span className="text-white font-semibold"> Only $59/vehicle/month</span> - 
                cancel anytime.
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="bg-white/10 backdrop-blur-xl border-white/20">
            <CardContent className="p-6">
              {/* Progress Steps */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= 1 ? 'bg-cyan-500 text-white' : 'bg-slate-600 text-slate-400'
                }`}>1</div>
                <div className={`w-16 h-1 rounded ${step >= 2 ? 'bg-cyan-500' : 'bg-slate-600'}`} />
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= 2 ? 'bg-cyan-500 text-white' : 'bg-slate-600 text-slate-400'
                }`}>2</div>
              </div>

              <form onSubmit={handleSubmit}>
                {step === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-white mb-4">Company Information</h3>
                    
                    <div>
                      <Label className="text-slate-200">Company Name *</Label>
                      <Input
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Your Transportation Company"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-slate-200">Company Address</Label>
                      <Input
                        name="company_address"
                        value={formData.company_address}
                        onChange={handleChange}
                        placeholder="123 Main St, City, State"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-200">Fleet Size</Label>
                      <select
                        name="vehicle_count"
                        value={formData.vehicle_count}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                      >
                        <option value="1-5" className="bg-slate-800">1-5 vehicles</option>
                        <option value="6-15" className="bg-slate-800">6-15 vehicles</option>
                        <option value="16-30" className="bg-slate-800">16-30 vehicles</option>
                        <option value="31+" className="bg-slate-800">31+ vehicles</option>
                      </select>
                    </div>

                    <Button
                      type="button"
                      onClick={() => {
                        if (!formData.company_name) {
                          toast.error("Please enter your company name");
                          return;
                        }
                        setStep(2);
                      }}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 mt-4"
                    >
                      Continue <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-white mb-4">Your Account</h3>
                    
                    <div>
                      <Label className="text-slate-200">Your Name *</Label>
                      <Input
                        name="admin_name"
                        value={formData.admin_name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-slate-200">Email Address *</Label>
                      <Input
                        name="admin_email"
                        type="email"
                        value={formData.admin_email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-slate-200">Phone Number</Label>
                      <Input
                        name="admin_phone"
                        value={formData.admin_phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-200">Password *</Label>
                      <PasswordInput
                        name="password"
                        data-testid="trial-password-input"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Min 6 characters"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-slate-200">Confirm Password *</Label>
                      <PasswordInput
                        name="confirm_password"
                        data-testid="trial-confirm-password-input"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        placeholder="Confirm your password"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>

                    <div className="flex gap-3 mt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="flex-1 border-white/20 text-white hover:bg-white/10"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500"
                      >
                        {isLoading ? "Creating..." : "Start Free Trial"}
                        <Sparkles className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </form>

              <p className="text-center text-slate-400 text-xs mt-6">
                By starting a trial, you agree to our Terms of Service and Privacy Policy.
                <br />No credit card required. Cancel anytime.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
